<template>
    <div 
        id="page-content"
        class="my-tv no-outline"
        tabindex="0"
        data-no-focus-scroll
        data-page="my-tv"
        @focusin="onFocus"
        @focusout="onFocusout"
    >
        <div class="my-tv__container">
            <Lines-my-tv 
                ref="linesMyTv" 
                @ready="readyLines"
                @show-all="showAllItems"
                @list-item-selected="itemsSelected"
                @plus="addItems"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
    import { AppConstants } from "@/common";
    import { linkItems } from "@/components/Header/types";
    import { PlayItem } from '@ca.vmedia.tv/domains';
    import type { OnDemandCardData, ProgramCardData, ChannelLiveCardData } from "@/components/Cards/types";
    import { useApplicationStore } from "@/stores/Application";
    import { Logger } from '@/utils';
    import { useFocus } from "@/composables/useFocus";
    import { ref, type Ref, onMounted, nextTick, onUnmounted, computed, watch } from "vue";
    import { useRouter } from 'vue-router';
    import { useParentControlStore } from "@/stores/ParentControl";
    import { storeToRefs } from 'pinia'
    import LinesMyTv from "@/components/widgets/LinesMyTv/LinesMyTv.vue";
    import type { LinesMyTvRef } from "@/components/widgets/LinesMyTv/types";
    import { usePlayerStore } from "@/stores/Player";
    import { useDetailPageStore } from "@/stores/DetailPage";
    import type { IEventSelected } from "@/components/MultiLineView/types";
    import { Strings } from '@/locales/types/enums';
    import { ScreenName } from "@/common/types/screen-names";
    

    definePageMeta({
        middleware: ['set-page-indicator', 'fetch-my-tv'],
    });

    const ApplicationStore = useApplicationStore();
    const {
        getIsHaveFocusedElement: isHavSelectedIdProgram,
    } = storeToRefs(ApplicationStore);
    const {
        setDisableRouteChange,
        setTooltipeShowing,
        setTooltipeMessage
    } = ApplicationStore;

    const { 
        setPlayBtnUnavailable,
        setViewedDetailItems
    } = useDetailPageStore();
    
    const { setIsHaveFocusedElement, focusElementLine, focusToHeader } = useFocus();

    const ParentControlStoreModule = useParentControlStore();
    const {
        getIsModalParentVisible: isTypeModeEnabledPin,
    } = storeToRefs(ParentControlStoreModule);
    const PlayerStoreModule = usePlayerStore();
    const {
        startPlayingChannel,
    } = PlayerStoreModule;


    const { t } = useI18n();
    const router = useRouter();
    const { $fire, $eventBus, $navigator, $analytics } = useNuxtApp();

    const traceMyTvScreen = $fire.trace($fire.performance, 'MY_TV');
    try {
        traceMyTvScreen.start();
        Logger.captureLog('MY_TV start');
    } catch (e){
        Logger.captureLog(e);
    } 

    const { shelfKeys}: typeof AppConstants = AppConstants;
    const isReadyLines: Ref<boolean> = ref(false);
    const linesMyTv: Ref<LinesMyTvRef | null> = ref(null);

    const isLoadedComponents = computed((): boolean => linesMyTv.value !== null);
    const isReadyAll = computed((): boolean => isReadyLines.value && isLoadedComponents.value);

    const readyLines = () => {
        isReadyLines.value = true;
    };
    const onFocus = () => {
        registerKeyListeners();
    };
    const onFocusout = () => {
        unregisterKeyListeners();
    };
    const registerKeyListeners = () => {
        $navigator.on("keypress:back", keyBackListener);
    };
    const unregisterKeyListeners = () => {
        $navigator.off("keypress:back", keyBackListener);
    };
    const keyBackListener = () => {
        focusToHeader();
    };
    const focusFirstRow = () => {
        if (linesMyTv.value) {
            linesMyTv.value.focusFirstRow();
        }
    };
    const focusElement = () => {
        if (isHavSelectedIdProgram.value) {
            focusElementLine();
        } else {
            focusToHeader();
        }
    };
    const  onAddChannels = () => {
        void router.push({ 'path': linkItems.settings.routerPath, query: {'linkItemCode': linkItems.favorites.code }});
        unregisterKeyListeners();
    };
    const  onAddOnDemand = () => {
        void router.push(linkItems.onDemand.routerPath);

        setDisableRouteChange(true);
    };
    const  onAddWatchList = () => {
        void router.push(linkItems.guide.routerPath);
    };
    const  onShowAllChannels = () => {
        void router.push(`${linkItems.showAllFavChannels.routerPath}`);
        unregisterKeyListeners();
    };
    const  onShowAllOnDemand = () => {
        void router.push(`${linkItems.showAllOnDemand.routerPath}`);
        unregisterKeyListeners();
    };
    const onShowAllWatchList = () => {
        void router.push(`${linkItems.showAllPrograms.routerPath}`);
        unregisterKeyListeners();
    };
    const gotoDetailsPageForVOD = (data: OnDemandCardData) => {
        try {
            const id = Number.parseInt(data.getId(), 10);
            if (Number.isNaN(id)) {
                throw new Error("Invalid ID: " + data.getId());
            }
            const playItem = data.getPlayItem();
            if (!playItem) {
                throw new Error("playItem is null:" + JSON.stringify(data));
            }
            setViewedDetailItems(playItem);
            const path = PlayItem.isSeries(playItem) ?
                `${linkItems.detailsVodSeries.routerPath}/${id}` :
                `${linkItems.detailsVodMovies.routerPath}/${id}`;
            void router.push(path);
            unregisterKeyListeners();
        } catch (error) {
            Logger.captureError(error as Error);
            setTooltipeMessage(t(Strings.DETAILS_TOOLTIP_ERROR_OPENING_DETAILS_PAGE).toString());
            setTooltipeShowing({visible: true});
        }
    };
    const gotoDetailsPage = (item: ProgramCardData) => {
        try {
            const id = Number.parseInt(item.getId(), 10);
            if (Number.isNaN(id)) {
                throw new Error("Invalid ID: " + item.getId());
            }

            const playItem = item.getPlayItem();
            if (!playItem) {
                throw new Error("playItem is null:" + JSON.stringify(item));
            }
            setViewedDetailItems(playItem);
            // Disable play btn
            setPlayBtnUnavailable(true);
            void router.push(`${linkItems.detailsPrograms.routerPath}/${playItem.id}`);
            unregisterKeyListeners();
        } catch (error) {
            Logger.captureError(error as Error);
            setTooltipeMessage(t(Strings.DETAILS_TOOLTIP_ERROR_OPENING_DETAILS_PAGE).toString());
            setTooltipeShowing({visible: true});
        }
    };

    const showAllItems = (val: IEventSelected) => {
        switch (val.lineId) {
            case shelfKeys.favouritesChannelsCards:
                onShowAllChannels();
                break;

            case shelfKeys.favouritesOnDemandsCards:
                onShowAllOnDemand();
                break;

            case shelfKeys.favouritesLiveTvCards:
                onShowAllWatchList();
                break;
        }
    };
    const itemsSelected = (val: IEventSelected) => {
        switch (val.lineId) {
            case shelfKeys.favouritesChannelsCards:
                onPlayChannel(val.item as ChannelLiveCardData);
                break;

            case shelfKeys.favouritesOnDemandsCards:
                gotoDetailsPageForVOD(val.item as OnDemandCardData);
                break;

            case shelfKeys.favouritesLiveTvCards:
                gotoDetailsPage(val.item as ProgramCardData);
                break;
        }
    };
    const addItems = (lineId: string) => {
        switch (lineId) {
            case shelfKeys.favouritesChannelsCards:
                onAddChannels();
                break;

            case shelfKeys.favouritesOnDemandsCards:
                onAddOnDemand();
                break;

            case shelfKeys.favouritesLiveTvCards:
                onAddWatchList();
                break;
        }
    };
    const onPlayChannel = (item: ChannelLiveCardData) => {
        void startPlayingChannel(item);
        unregisterKeyListeners();
    };

    const handlerReady = (): void => {
        void nextTick(()=>{
            setIsHaveFocusedElement();
            if (!isTypeModeEnabledPin.value) {
                focusElement();
            }
        });

        if (traceMyTvScreen) {
            try {
                traceMyTvScreen.stop();
                Logger.captureLog(`MY_TV stop ${traceMyTvScreen.durationUs / 1000000} sec`);
            } catch (e){
                Logger.captureLog(e);
            }
        }
    };

    onUnmounted(() => {
        $eventBus.off("key-up-rows", focusToHeader);
        $eventBus.off("leave-header", focusFirstRow);
    });

    onMounted(() => {
        void nextTick((): void => {
            $eventBus.on("key-up-rows", focusToHeader);
            $eventBus.on("leave-header", focusFirstRow);

            $analytics.createScreenEvent(ScreenName.MY_TV_PAGE, ScreenName.MY_TV_PAGE);
        });
    });

    watch(
        isReadyAll, 
        (value: boolean) => {
            if (value) {
                handlerReady();
            }
        }
    );
</script>

<style lang="scss">
    @import url("styles.scss");
</style>
